"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Subscription gate",
    rule: "Only creating a customer, single or bulk, is limited by your plan. Editing, deleting, linking, addresses, and every dropdown are not",
  },
  {
    topic: "Permission gap on Bulk Add",
    rule: "Adding one customer at a time requires the Create permission. Bulk Add currently doesn't check that permission at all, only whether the feature is on your plan",
  },
  {
    topic: "Display name uniqueness",
    rule: "Checked without regard to letter case. A Customer and a Vendor can share the same display name, but two Customers cannot use the same name, and neither can two Vendors",
  },
  {
    topic: "Currency locks after saving",
    rule: "Required when you create the customer. The picker is Settings → Currencies. Cannot be changed once saved",
  },
  {
    topic: "Restriction Payment Terms Document is the overdue block",
    rule: "If Quote, Sales Order, Delivery Challan, or Invoice is listed, creating that document is blocked while this customer has an Overdue invoice. Company defaults are Settings → Credit limits until the customer overrides them",
  },
  {
    topic: "Opening Balance sign chooses the settlement module",
    rule: "Positive Opening Balance (they owe you) is collected in Payments Received as (Customer opening balance). Negative Opening Balance (they have credit) is applied to invoices with Apply Credit / Pay Against Invoices / Auto Map Opening Balance — it is not a Payment Received. You cannot flip the sign or reduce below already-applied amounts until those applications are removed",
  },
  {
    topic: "Negative credit is applied branch by branch",
    rule: "Opening Balance lives on each branch row even when Branch Wise Credit Limit is off. Pay Against Invoices on the customer detail only lists branches whose Opening Balance is still negative, then you pick which branch's credit to map. Apply Credit on an invoice is locked to that invoice's branch. Auto Map on the customer maps only this customer; Auto Map on the Invoices list maps remaining credit across customers",
  },
  {
    topic: '"Utilized credit" means two different things',
    rule: "The per-customer credit dashboard adds up unpaid invoice balances. The credit overview and utilization reports instead use each branch's balance. The two numbers can disagree",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. The core identity fields" },
      { heading: "2. Tax, currency, and profile details" },
      { heading: "3. Addresses: one billing, one default shipping, many additional" },
      { heading: "4. Branches: where credit limit, payment terms, and balances actually live" },
      { heading: "5. Opening balance — they owe you vs you owe them" },
      { heading: "6. Credit limit and unused credit" },
      { heading: "7. Linking a Customer to a Vendor" },
      { heading: "8. Adding one at a time vs. Bulk Add" },
      { heading: "9. Viewing full customer details" },
      { heading: "10. Deleting a customer" },
      { heading: "11. Where a customer gets picked elsewhere" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p key={item.term} className="text-[16px] geist-regular text-[#374151] leading-relaxed">
        <span className="geist-medium text-[#111827]">{item.term}:</span> {item.desc}
      </p>
    ))}
  </div>
);

export default function CustomerMasterPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Customer
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone recording who this company sells to, buys from, or both
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Customer</B> record is a lot bigger than a name and an
            address. It carries <B>GST and tax details</B>, a{" "}
            <B>currency</B>, <B>portal access</B>, a <B>price list</B>, and
            per-branch <B>balances</B> and <B>credit limits</B>. When a
            company you sell to is also one you buy from, you can{" "}
            <B>link</B> that Customer to a matching <B>Vendor</B> record so
            both sides show up together. This page walks through every one
            of those pieces, in plain terms.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Core identity fields */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. The core identity fields
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            These sit at the very top of the Add Customer form, always
            visible, no tab to click into first.
          </p>
          <Definitions
            items={[
              {
                term: "Customer ID",
                desc: (
                  <>
                    <B>Filled in automatically</B> from Customer Number
                    Configuration on Add Customer (prefix, next number,
                    auto-generate). You don&apos;t type a one-off ID on the
                    form — change that numbering setting instead.
                  </>
                ),
              },
              {
                term: "Primary Contact Name",
                desc: (
                  <>
                    Labelled <B>BP Primary Contact Name</B> on the form (
                    <B>BP</B> just means the business or person this
                    customer represents). <B>First name is required</B>,
                    last name is optional.
                  </>
                ),
              },
              {
                term: "Business Partner Type",
                desc: (
                  <>
                    Are you dealing with a <B>Business</B> or an{" "}
                    <B>Individual</B>? Required.
                  </>
                ),
              },
              {
                term: "Category",
                desc: (
                  <>
                    Whether this customer buys <B>Goods</B>,{" "}
                    <B>Services</B>, or both. You can tick both boxes if
                    both apply.
                  </>
                ),
              },
              {
                term: "Company Name and Customer Display Name",
                desc: (
                  <>
                    Both required. The <B>Display Name</B> must be{" "}
                    <B>different</B> from every other customer you have, so
                    you don&apos;t end up with the same one twice.
                  </>
                ),
              },
              {
                term: "Account Type",
                desc: (
                  <>
                    Always shows <B>Accounts Receivable</B>, the ledger
                    account that tracks what this customer owes you. You
                    don&apos;t pick this, it&apos;s set automatically
                    because this is a customer.
                  </>
                ),
              },
              {
                term: "Primary Email",
                desc: (
                  <>
                    Labelled <B>BP Primary Email</B>. Only required if you
                    turn on <B>portal access</B> for this customer.
                  </>
                ),
              },
              {
                term: "Customer Phone Number",
                desc: <>Numbers only. Optional.</>,
              },
              {
                term: "Company Logo",
                desc: (
                  <>
                    One image, up to <B>5MB</B>.
                  </>
                ),
              },
              {
                term: "Code",
                desc: (
                  <>
                    A separate, free-text field from Customer ID. Entirely{" "}
                    <B>optional</B>, use it however makes sense for you.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: a saved customer, Customer ID CUS-00567, Company Name ABSS, Category ticked as Goods, Status Active." />

          {/* 2. Tax, currency, profile */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Tax, currency, and profile details
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            All of this lives under the <B>Other Details</B> tab of the
            customer form.
          </p>
          <Definitions
            items={[
              {
                term: "GST Treatment",
                desc: (
                  <>
                    <B>Registered Business Regular</B>,{" "}
                    <B>Unregistered Business Regular</B>, or <B>Export</B>.
                    Required, and it changes what else you need to fill in.
                  </>
                ),
              },
              {
                term: "GSTIN / UIN",
                desc: (
                  <>
                    Only required when GST Treatment is{" "}
                    <B>Registered Business Regular</B>.
                  </>
                ),
              },
              {
                term: "Country and Import-Export Code",
                desc: (
                  <>
                    For <B>Export</B> customers. Saving actually requires
                    both once you pick Export, even though the form
                    doesn&apos;t visibly flag them as required, so it&apos;s
                    worth filling them in anyway.
                  </>
                ),
              },
              {
                term: "Tax Preference",
                desc: (
                  <>
                    <B>Taxable</B> or <B>Tax Exempt</B>.
                  </>
                ),
              },
              {
                term: "PAN",
                desc: <>Optional, checked against a valid PAN format.</>,
              },
              {
                term: "Currency",
                desc: (
                  <>
                    Required, and <B>locked for good</B> the moment you save
                    the customer. The list comes from{" "}
                    <B>Settings → Currencies</B>. There&apos;s no changing it
                    later.
                  </>
                ),
              },
              {
                term: "Price List",
                desc: (
                  <>
                    Optional. Turn on{" "}
                    <B>Include Price List Item Only</B> next to it and
                    this customer will only be able to buy items that
                    appear on that Price List. Vendors don&apos;t have this
                    option.
                  </>
                ),
              },
              {
                term: "Portable Language",
                desc: (
                  <>
                    The language their <B>portal</B> shows in. Defaults to{" "}
                    <B>English</B>.
                  </>
                ),
              },
              {
                term: "Enable Portal?",
                desc: (
                  <>
                    Turns on portal login for this customer. A working{" "}
                    <B>email</B> is needed for it to work.
                  </>
                ),
              },
              {
                term: "Attach Documents",
                desc: (
                  <>
                    Up to <B>three files</B>, 5MB each.
                  </>
                ),
              },
              {
                term: "Website, Department, Designation, Twitter, Skype, Facebook",
                desc: (
                  <>
                    All optional. Just extra contact details, tucked under{" "}
                    <B>Add More Details</B>.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: GST Treatment set to Unregistered Business Regular, Currency showing ₹ - INR (Indian Rupee), Enable Portal switched to No." />

          {/* 3. Addresses */}
          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Addresses: one billing, one default shipping, many additional
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The Address tab on the main form only covers the first two.
            Extra shipping addresses are added separately, from the
            customer&apos;s own detail page.
          </p>
          <Definitions
            items={[
              {
                term: "Billing Address",
                desc: (
                  <>
                    One fixed address. It can <B>never be turned off</B>.
                  </>
                ),
              },
              {
                term: "Shipping Address (default)",
                desc: (
                  <>
                    Also one fixed address, also can{" "}
                    <B>never be turned off</B>. A{" "}
                    <B>Copy Billing Address</B> button can fill it in for
                    you.
                  </>
                ),
              },
              {
                term: "Additional Shipping Addresses",
                desc: (
                  <>
                    A real, separate list of extra delivery addresses. Each
                    one has its own <B>Active</B> or <B>Inactive</B>{" "}
                    switch.
                  </>
                ),
              },
              {
                term: "How the default gets filled",
                desc: (
                  <>
                    If there&apos;s no default shipping address yet, the
                    first one you add <B>becomes</B> the default. Only
                    after that does anything new go into the additional
                    list.
                  </>
                ),
              },
              {
                term: "Turning an address off",
                desc: (
                  <>
                    Only ever affects an <B>additional</B> address. The
                    billing address and the default shipping address are{" "}
                    <B>always available</B>, no matter what.
                  </>
                ),
              },
              {
                term: "What's required, and where",
                desc: (
                  <>
                    On the main customer form, no address field is actually
                    required. Adding or editing an address from the{" "}
                    <B>detail page</B> does require Country, Street, City,
                    State, and Pin Code.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Additional Address table with the Active column showing Yes for every row listed." />

          {/* 4. Branches */}
          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Branches: where credit limit, payment terms, and balances actually live
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            If your company uses branches, every active one gets its own
            row here, whether you turn on branch-specific settings or not.
          </p>
          <Definitions
            items={[
              {
                term: "Every branch's row",
                desc: (
                  <>
                    Shows an <B>Active</B> switch, the{" "}
                    <B>Opening Balance</B>, and the{" "}
                    <B>Current Balance</B>, one row per active branch. Those
                    two balances are always per branch. Turning{" "}
                    <B>Branch Wise Credit Limit</B> or{" "}
                    <B>Branch Wise Payment Terms</B> off does not merge
                    Opening Balance into one company total.
                  </>
                ),
              },
              {
                term: "Branch Wise Credit Limit",
                desc: (
                  <>
                    A switch on this customer, not a company-wide setting.{" "}
                    <B>Off</B> means one overall Credit Limit for the whole
                    customer. <B>On</B> means every branch gets its own
                    limit.
                  </>
                ),
              },
              {
                term: "Branch Wise Payment Terms",
                desc: <>The same idea, but for Payment Terms.</>,
              },
              {
                term: "Restriction Document",
                desc: (
                  <>
                    Pick exactly which documents (Quote, Sales Order,
                    Delivery Challan, Invoice) the <B>credit limit amount</B>{" "}
                    is checked against. Anything you don&apos;t pick isn&apos;t
                    checked. Company defaults for new customers live under{" "}
                    <B>Settings → Credit limits</B> until this customer
                    overrides them.
                  </>
                ),
              },
              {
                term: "Restriction Payment Terms Document",
                desc: (
                  <>
                    If a document type is listed here, you cannot{" "}
                    <B>create</B> that document while this customer has an{" "}
                    <B>Overdue</B> invoice. That is the overdue block — not a
                    separate toggle. Company defaults live under{" "}
                    <B>Settings → Credit limits</B>.
                  </>
                ),
              },
              {
                term: "Changing Opening Balance later",
                desc: (
                  <>
                    Doesn&apos;t reset Current Balance, it{" "}
                    <B>adjusts it by the difference</B> instead.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Credit Limit set to ₹3,00,000, Restriction Document listing Quote, Sales Order, Delivery Challan, Invoice, and nine branch rows each marked Active: Yes." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Opening balance — they owe you vs you owe them
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Each branch row has an <B>Opening Balance</B>. The sign is not
            decoration — it chooses which module settles it later.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <div className="border border-[#F4C7A8] rounded-[12px] p-4 bg-[#FFF7F2]">
              <p className="text-[12px] geist-medium uppercase tracking-wide text-[#DF5D01] mb-1">
                Positive Opening Balance
              </p>
              <p className="text-[15px] sentient-medium text-[#111827] mb-1">
                They owe you
              </p>
              <p className="text-[13px] geist-regular text-[#374151] leading-relaxed">
                Stored as an <B>opening receivable</B>. Collect it in{" "}
                <B>Payments Received</B> like an invoice. The payment picker
                shows <B>(Customer opening balance)</B>. Approving that payment
                reduces remaining opening balance and Current Balance. You do
                not Mark As anything on the customer for this.
              </p>
            </div>
            <div className="border border-[#E5E5E5] rounded-[12px] p-4 bg-[#EFF8FF]">
              <p className="text-[12px] geist-medium uppercase tracking-wide text-[#1785B6] mb-1">
                Negative Opening Balance
              </p>
              <p className="text-[15px] sentient-medium text-[#111827] mb-1">
                You owe them / they have credit
              </p>
              <p className="text-[13px] geist-regular text-[#374151] leading-relaxed">
                Stored as <B>customer credit</B>. This is <B>not</B> a Payment
                Received. When any branch is negative, customer detail shows{" "}
                <B>Auto Map Opening Balance</B> and <B>Pay Against Invoices</B>.
                On an open invoice for that branch, the button is{" "}
                <B>Apply Credit</B>.
              </p>
            </div>
          </div>
          <Definitions
            items={[
              {
                term: "Current Balance",
                desc: (
                  <>
                    Starts from Opening Balance and then moves as invoices and
                    payments post. Changing Opening Balance later adjusts
                    Current Balance by the difference — it does not wipe the
                    history. The customer detail also shows{" "}
                    <B>Total Opening Balance</B> and{" "}
                    <B>Total Current Balance</B> as the sum of the branch
                    rows.
                  </>
                ),
              },
              {
                term: "Pay Against Invoices (customer detail)",
                desc: (
                  <>
                    On the customer header when <B>any</B> branch Opening
                    Balance is still negative. Opens{" "}
                    <B>Apply Opening Balance to Invoices</B>. The{" "}
                    <B>Branch</B> picker lists only those negative-OB
                    branches — credit on one branch cannot pay invoices of
                    another unless you turn off{" "}
                    <B>Show current branch documents only</B>. Enter{" "}
                    <B>Balance to Apply</B> on Sent or Partially Paid
                    invoices, then <B>Apply To Invoices</B>. This does not
                    create a Payment Received.
                  </>
                ),
              },
              {
                term: "Auto Map Opening Balance (customer detail)",
                desc: (
                  <>
                    Same header, same negative-OB condition. Maps this
                    customer&apos;s remaining credit onto open invoices,
                    oldest due date first. Confirm modal: Auto Map Opening
                    Balance to Invoices. This is this customer only. The
                    Invoices list TOOLS action maps remaining credit across
                    customers.
                  </>
                ),
              },
              {
                term: "Apply Credit (invoice detail)",
                desc: (
                  <>
                    When you are already on an unpaid invoice whose branch
                    still has remaining credit, the invoice header shows{" "}
                    <B>Apply Credit</B>. That modal locks the branch to the
                    invoice&apos;s branch — no branch picker.
                  </>
                ),
              },
              {
                term: "Opening balance payments on the customer detail",
                desc: (
                  <>
                    Under Transactions, the{" "}
                    <B>Opening balance payments</B> panel appears when
                    history exists. Positive-side source is Payment
                    Received (they owed you). Negative-side source is
                    Invoice (credit mapped onto invoices).
                  </>
                ),
              },
              {
                term: "Add Customer Opening Balance (list)",
                desc: (
                  <>
                    From the customer list you can upload opening balances in
                    bulk, one amount per branch column. Same sign rules apply
                    once those amounts exist.
                  </>
                ),
              },
            ]}
          />
          <Callout type="warning">
            You can&apos;t lower a branch&apos;s Opening Balance below what
            has already been applied, and you can&apos;t flip it from a debt
            to a credit (or back) while those applications exist. For a
            positive balance, remove the Payment Received applications first.
            For a negative balance, remove the invoice credit mappings first.
          </Callout>
          <Screenshot caption="Screenshot: customer branch Opening Balance +12,000 vs −8,000, with Auto Map Opening Balance and Pay Against Invoices on the detail header when a branch is negative." />

          {/* 6. Credit limit */}
          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Credit limit and unused credit
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Three numbers you&apos;ll see around the customer screens, each
            worked out a little differently.
          </p>
          <Definitions
            items={[
              {
                term: "Total Credit Limit",
                desc: (
                  <>
                    Adds up every active branch&apos;s limit when{" "}
                    <B>Branch Wise Credit Limit</B> is on. Otherwise it&apos;s
                    just the one overall limit.
                  </>
                ),
              },
              {
                term: "Utilized credit",
                desc: (
                  <>
                    Shown two different ways depending where you look: the
                    per-customer credit dashboard totals up{" "}
                    <B>unpaid invoices</B>, while the overview and
                    utilization reports use each branch&apos;s{" "}
                    <B>balance</B> instead. The two can differ.
                  </>
                ),
              },
              {
                term: "Unused credit",
                desc: (
                  <>
                    On the customer list this is the total{" "}
                    <B>available balance</B> across every Credit Note this
                    customer has. It is <B>not</B> negative Opening Balance
                    (that credit is applied with Apply Credit / Pay Against
                    Invoices) and it is not Unused Amount on a Payment
                    Received.
                  </>
                ),
              },
              {
                term: "On the Vendor side",
                desc: (
                  <>
                    Same idea, but added up from <B>Debit Notes</B>{" "}
                    instead of Credit Notes.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Total Credit Limit ₹3,00,000 next to a Current Balance of ₹95,960 for the same customer." />

          {/* 6. Vendor linking */}
          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Linking a Customer to a Vendor
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            When a company you sell to is also one you buy from, linking
            them lets you see both sides without switching screens.
          </p>
          <Definitions
            items={[
              {
                term: "What linking does",
                desc: (
                  <>
                    Connects this Customer to a Vendor you pick. From then
                    on, opening either one shows you the{" "}
                    <B>other one&apos;s details and transactions</B> too.
                  </>
                ),
              },
              {
                term: "Only one link each",
                desc: (
                  <>
                    A Vendor already linked to a different Customer{" "}
                    <B>can&apos;t be picked again</B>. You&apos;d need to
                    unlink that pair first.
                  </>
                ),
              },
              {
                term: "Unlinking or changing the link",
                desc: (
                  <>
                    Both sides update <B>at the same time</B>. There&apos;s
                    no moment where only one side shows the change.
                  </>
                ),
              },
              {
                term: "Deleting a linked Customer or Vendor",
                desc: (
                  <>
                    <B>Blocked</B> while the link is still in place. Unlink
                    first, then delete.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Linked to the Vendor checkbox ticked on, with a vendor name selected right below it." />

          {/* 7. Single vs bulk */}
          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Adding one at a time vs. Bulk Add
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Bulk Add checks more than you might expect, and skips less.
          </p>
          <Definitions
            items={[
              {
                term: "Adding one customer",
                desc: (
                  <>
                    Checks the <B>display name is unique</B>, checks the
                    Vendor link if you set one, and sends a{" "}
                    <B>portal invite</B> if you turned that on. If the
                    invite email fails to send, the customer is{" "}
                    <B>still saved</B>.
                  </>
                ),
              },
              {
                term: "Bulk Add still checks for duplicate names",
                desc: (
                  <>
                    Both <B>within the file</B> you upload and against your{" "}
                    <B>existing customers</B>. This step is not skipped.
                  </>
                ),
              },
              {
                term: "What Bulk Add does skip",
                desc: (
                  <>
                    Only the <B>Vendor link</B> step. Everyone added in
                    bulk comes in as a standalone customer.
                  </>
                ),
              },
              {
                term: "All rows succeed, or none do",
                desc: (
                  <>
                    If even one row in the file fails a check, the{" "}
                    <B>whole upload is rejected</B>. Nothing from it gets
                    saved.
                  </>
                ),
              },
              {
                term: "Every branch, automatically",
                desc: (
                  <>
                    Bulk-added customers are attached to{" "}
                    <B>every active branch</B> with a zero opening
                    balance right away, something a single Add doesn&apos;t
                    do for you.
                  </>
                ),
              },
              {
                term: "Add Customer Opening Balance",
                desc: (
                  <>
                    Separate from Bulk Add Customers. On the customer list,
                    IMPORT &amp; EXPORT includes{" "}
                    <B>Add Customer Opening Balance</B> — upload amounts
                    using branch names as columns. That is how you set
                    opening balances in bulk after the customers already
                    exist.
                  </>
                ),
              },
              {
                term: "No limit on file size",
                desc: <>You can upload as many rows at once as you need.</>,
              },
            ]}
          />
          <Screenshot caption="Screenshot: an uploaded row highlighted in red with the message 'Customer with this name already exists'." />

          {/* 8. Viewing details */}
          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Viewing full customer details
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Opening a customer brings together every document that
            actually mentions them, plus the branch balances and the
            opening-balance actions when they apply.
          </p>
          <Definitions
            items={[
              {
                term: "Header buttons",
                desc: (
                  <>
                    <B>General Ledger</B> and <B>360 View</B> are always
                    there (360 View is customers only). When{" "}
                    <B>any branch Opening Balance is negative</B>, the
                    header also shows <B>Auto Map Opening Balance</B> and{" "}
                    <B>Pay Against Invoices</B>. Those two are how remaining
                    credit is mapped — see section 5.
                  </>
                ),
              },
              {
                term: "Overview — branch table",
                desc: (
                  <>
                    <B>Branch Wise Credit Limit</B> and{" "}
                    <B>Branch Wise Payment Terms</B> show as Yes or No.
                    Totals for Opening Balance and Current Balance sit
                    above the table. Every branch row still lists{" "}
                    <B>Opening Balance</B> and <B>Current Balance</B>.
                    Credit Limit and Restriction Document columns appear
                    only when Branch Wise Credit Limit is on; Payment Terms
                    columns appear only when Branch Wise Payment Terms is
                    on.
                  </>
                ),
              },
              {
                term: "What you see for the customer",
                desc: (
                  <>
                    Under Transactions: <B>Invoices</B>,{" "}
                    <B>Delivery Challans</B>, <B>Sales Orders</B>,{" "}
                    <B>Quotes</B>, <B>Payments Received</B>, and{" "}
                    <B>Credit Notes</B>, each with its own status and
                    balance. If opening-balance history exists,{" "}
                    <B>Opening balance payments</B> is another panel in
                    that same list.
                  </>
                ),
              },
              {
                term: "If they're also linked as a Vendor",
                desc: (
                  <>
                    You also see their <B>Purchase Orders</B>,{" "}
                    <B>GRPOs</B>, <B>Bills</B>, <B>Payments Made</B>, and{" "}
                    <B>Debit Notes</B>, right alongside the customer data.
                    A linked vendor detail uses the same two header buttons
                    against Bills when a vendor branch is negative.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            The 360 View button is there, but it didn&apos;t load
            successfully while checking this page, so what it actually
            shows isn&apos;t confirmed here. Treat it as newer and worth
            trying yourself before relying on it.
          </Callout>
          <Screenshot caption="Screenshot: customer header with Auto Map Opening Balance and Pay Against Invoices, plus the branch table showing Opening Balance and Current Balance on every row." />

          {/* 9. Delete */}
          <h3
            id="section-0-sub-9"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            10. Deleting a customer
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            This is checked far more carefully than it looks. It&apos;s not
            just a simple ownership check.
          </p>
          <Definitions
            items={[
              {
                term: "Blocked by any real transaction",
                desc: (
                  <>
                    A <B>Quote</B>, <B>Sales Order</B>, <B>Invoice</B>,{" "}
                    <B>Delivery Challan</B>, <B>Payment Received</B>,{" "}
                    <B>Credit Note</B>, <B>Recurring Invoice</B>,{" "}
                    <B>Customer Order</B>, <B>Rejection Order</B>, or{" "}
                    <B>Expense</B> against them stops the delete. On the
                    Vendor side, the same is true for a{" "}
                    <B>Purchase Order</B>, <B>GRPO</B>, <B>Bill</B>,{" "}
                    <B>Payment Made</B>, <B>Recurring Bill</B>,{" "}
                    <B>Recurring Expense</B>, or <B>Debit Note</B>.
                  </>
                ),
              },
              {
                term: "Also blocked when",
                desc: (
                  <>
                    They&apos;re set as an item&apos;s preferred vendor or
                    specific customer, they have an{" "}
                    <B>active portal account</B> or an unanswered{" "}
                    <B>portal invite</B>, a CRM call is logged against
                    them, or they&apos;re currently <B>linked</B> to
                    another party.
                  </>
                ),
              },
              {
                term: "The one thing that doesn't block it",
                desc: (
                  <>
                    Their own opening balance entry. Deleting the customer
                    simply <B>reverses</B> that entry instead of blocking
                    the delete.
                  </>
                ),
              },
            ]}
          />
          <Callout type="warning">
            In practice, a customer or vendor with any real history
            essentially can&apos;t be deleted. And there&apos;s no
            soft-delete safety net either, once it goes through, it&apos;s
            permanent.
          </Callout>
          <Screenshot caption="Screenshot: a red error banner reading 'Cannot delete this customer because it is referenced in one or more existing documents'." />

          {/* 10. Dropdowns */}
          <h3
            id="section-0-sub-10"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            11. Where a customer gets picked elsewhere
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Screens elsewhere that let you pick a customer or vendor draw
            from three genuinely different lists.
          </p>
          <Definitions
            items={[
              {
                term: "The full-featured list",
                desc: (
                  <>
                    Used on sales documents. Shows each customer&apos;s{" "}
                    <B>unused credit</B> and their assigned{" "}
                    <B>Price List</B> right there in the picker.
                  </>
                ),
              },
              {
                term: "The simple list",
                desc: (
                  <>
                    Powers the <B>Linked to Vendor</B> or{" "}
                    <B>Linked to Customer</B> picker. Hides anyone
                    who&apos;s <B>already linked</B> to someone else.
                  </>
                ),
              },
              {
                term: "The combined list",
                desc: (
                  <>
                    Shows customers and vendors <B>together</B>, for
                    screens that don&apos;t care which type you pick.
                  </>
                ),
              },
              {
                term: "One thing all three share",
                desc: (
                  <>
                    They only ever show customers or vendors marked{" "}
                    <B>Active</B>.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: the customer dropdown on an Invoice, each name followed by its unused credit amount in brackets." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr key={r.topic} className={i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""}>
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
          <Link
            href="/documentation/books/purchase/vendors"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Vendors <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where you manage the companies and people you buy from
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/price-list"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Price List <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              What a customer's assigned Price List actually does
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/invoices"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Invoices <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where Apply Credit on an invoice uses this customer&apos;s
              remaining branch credit
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/payments-received"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Payments Received <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              How a positive Opening Balance is collected
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
